import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { Shadows } from 'theme';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';

interface Props {
  leadLists: any[];

  handleViewList(id: string): void;

  handleDeleteClick(id: string): void;
}

const LeadListTable: React.FC<Props> = ({ leadLists, handleViewList, handleDeleteClick }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasLists = leadLists && leadLists.length > 0;

  return (
    <div className={classes.wrapper}>
      <div className={classes.columnHeader}>
        <div className={classes.colList}>{t('tableHeaderName')}</div>
        <div className={classes.colRegion}>{t('tableHeaderRegion')}</div>
        <div className={cx(classes.colLeads, classes.alignRight)}>{t('tableHeaderLeads')}</div>
        <div className={cx(classes.colCreated, classes.alignRight)}>{t('tableHeaderCreated')}</div>
        <div className={classes.colActions} />
      </div>

      <div className={classes.rows}>
        {hasLists ? (
          leadLists.map((list) => (
            <div
              key={list._id}
              className={classes.row}
              role="button"
              tabIndex={0}
              onClick={() => handleViewList(list._id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleViewList(list._id);
                }
              }}
            >
              <div className={cx(classes.colList, classes.listCell)}>
                <span className={classes.name}>{list.name}</span>
                <span className={classes.product}>{list.product}</span>
              </div>
              <div className={cx(classes.colRegion, classes.regionCell)}>{list.region}</div>
              <div className={cx(classes.colLeads, classes.alignRight, classes.leadsCell)}>
                {Number(list.metadata?.leadCount ?? 0).toLocaleString()}
              </div>
              <div className={cx(classes.colCreated, classes.alignRight, classes.createdCell)}>
                {new Date(list.createdAt).toLocaleDateString()}
              </div>
              <div className={cx(classes.colActions, classes.actionsCell)}>
                <Tooltip title={t('edit')}>
                  <IconButton
                    className={classes.iconButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewList(list._id);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('delete')}>
                  <IconButton
                    className={cx(classes.iconButton, 'delete-button')}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(list._id);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          ))
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant={'body1'}>{t('noLeadListsFound')}</Typography>
            <Button variant={'text'} color={'primary'} onClick={() => navigate('/lead-list-research')} sx={{ mt: 1 }}>
              {t('generateFirstLeadList')}
            </Button>
          </Box>
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '0 24px 12px 24px',
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    color: theme.palette.greys.medium,
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingBottom: '8px',
  },
  row: {
    background: theme.palette.common.white,
    borderRadius: '12px',
    padding: '18px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'box-shadow 120ms ease-in-out',
    '&:hover': {
      boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    },
  },
  colList: {
    flex: 1,
    minWidth: 0,
  },
  colRegion: {
    width: '200px',
    flexShrink: 0,
  },
  colLeads: {
    width: '120px',
    flexShrink: 0,
  },
  colCreated: {
    width: '140px',
    flexShrink: 0,
  },
  colActions: {
    width: '100px',
    flexShrink: 0,
  },
  alignRight: {
    textAlign: 'right',
  },
  listCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  name: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    color: theme.palette.primary.main,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  product: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  regionCell: {
    fontSize: theme.typography.pxToRem(14),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  leadsCell: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  createdCell: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  iconButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    padding: 0,
    color: theme.palette.greys.medium,
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(18),
    },
    '&:hover': {
      backgroundColor: theme.palette.backgrounds.main_new,
      color: theme.palette.primary.main,
      '&.delete-button': {
        backgroundColor: theme.palette.backgrounds.error,
        color: theme.palette.error.main,
      },
    },
  },
  emptyState: {
    background: theme.palette.common.white,
    borderRadius: '12px',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default LeadListTable;
